/**
 * One stored security scan, read top down: what the software list was read
 * from, what is new since the last complete scan, the vulnerabilities, the
 * other findings, and what the scan could not see. The last few scans follow
 * as one line each.
 *
 * Every sentence comes from the catalogue by the finding's code
 * (`findingMessage`, and `newsMessage` for a new vulnerability, which is named
 * by its software, version and severity rather than by the feed's title); the
 * server's words are for its own mail and timeline.
 */
import type { ReactNode } from "react";
import { AlertTriangle, CircleCheck, EyeOff } from "lucide-react";
import {
  findingMessage,
  historyCounts,
  levelTone,
  newsMessage,
  noteKey,
  verdictTone,
  type SecurityFindingView,
  type SecurityHistoryRow,
  type SecurityMessage,
  type SecurityScanView,
} from "../../../../lib/monitoringSecurity";
import { StatusPill, fmtDate, fmtDateTime } from "../../../ui";
import type { T } from "../words";
import { VulnList } from "./VulnList";

function FindingRows({ findings, message, t }: { findings: SecurityFindingView[]; message: (f: SecurityFindingView) => SecurityMessage; t: T }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {findings.map((f) => {
        const m = message(f);
        return (
          <li key={f.key} className="flex items-start gap-2.5 text-[13px]">
            <StatusPill tone={levelTone(f.level)} className="shrink-0">
              {t(`monitoring.security.level.${f.level}`)}
            </StatusPill>
            <span className="min-w-0 break-words pt-0.5">{t(m.key, m.vars)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="crm-prow-n mb-2">{title}</p>
      {children}
    </div>
  );
}

function News({ scan, t }: { scan: SecurityScanView; t: T }) {
  const diff = scan.diff;
  if (!diff) return <p className="crm-sub text-[13px]">{t("monitoring.security.firstScan")}</p>;
  return (
    <>
      {diff.added.length > 0 ? (
        <FindingRows
          findings={diff.added}
          // The rating sits mid-sentence ("CVSS 9.8, kriitiline"), so it is lower case there.
          message={(f) => newsMessage(f, scan.vulns, (rating) => t(`monitoring.security.cvss.${rating}`).toLocaleLowerCase())}
          t={t}
        />
      ) : (
        <p className="crm-sub text-[13px]">{t("monitoring.security.noNews", { when: fmtDate(diff.since) })}</p>
      )}
      {diff.resolved.length > 0 ? (
        <p className="crm-sub mt-2 flex items-center gap-2 text-[13px]">
          <CircleCheck className="size-4 shrink-0 text-[var(--color-success)]" />
          {t("monitoring.security.resolved", { n: diff.resolved.length })}
        </p>
      ) : null}
    </>
  );
}

function History({ history, t }: { history: SecurityHistoryRow[]; t: T }) {
  if (history.length < 2) return null;
  return (
    <Section title={t("monitoring.security.historyTitle")}>
      <ul className="flex flex-col gap-1 text-[12.5px]">
        {history.slice(1).map((h) => (
          <li key={h.at} className="flex flex-wrap items-center gap-2">
            <span className="crm-mut crm-tnum w-[88px] shrink-0">{fmtDate(h.at)}</span>
            <StatusPill tone={verdictTone(h.verdict)}>{t(`monitoring.security.verdict.${h.verdict}`)}</StatusPill>
            <span className="crm-sub">{historyCounts(h).map((m) => t(m.key, m.vars)).join(", ")}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function ScanDetails({ scan, history, t }: { scan: SecurityScanView; history: SecurityHistoryRow[]; t: T }) {
  const inv = scan.inventory;
  const source =
    inv.source === "reporter"
      ? t("monitoring.security.inventory.reporter", { when: fmtDateTime(inv.reportAt) })
      : t(`monitoring.security.inventory.${inv.source}`);
  const versions = [inv.core ? `WordPress ${inv.core}` : null, inv.php ? `PHP ${inv.php}` : null].filter(Boolean).join(", ");
  const vulnsChecked = !scan.notes.includes("feed_missing") && !scan.notes.includes("feed_stale") && inv.wordpress;

  return (
    <div className="flex flex-col gap-5 px-[26px] pb-5">
      <p className="crm-sub text-[13px]">
        {source}
        {versions ? ` · ${versions}` : ""}
      </p>

      <Section title={t("monitoring.security.newTitle")}>
        <News scan={scan} t={t} />
      </Section>

      <Section title={t("monitoring.security.vulnsTitle")}>
        {scan.vulns.length > 0 ? (
          <VulnList vulns={scan.vulns} t={t} />
        ) : (
          <p className="crm-sub flex items-center gap-2 text-[13px]">
            {vulnsChecked ? <CircleCheck className="size-4 shrink-0 text-[var(--color-success)]" /> : <EyeOff className="crm-mut size-4 shrink-0" />}
            {t(vulnsChecked ? "monitoring.security.noVulns" : "monitoring.security.vulnsNotChecked")}
          </p>
        )}
      </Section>

      <Section title={t("monitoring.security.findingsTitle")}>
        {scan.findings.length > 0 ? (
          <FindingRows findings={scan.findings} message={findingMessage} t={t} />
        ) : (
          <p className="crm-sub flex items-center gap-2 text-[13px]">
            <CircleCheck className="size-4 shrink-0 text-[var(--color-success)]" />
            {t("monitoring.security.noFindings")}
          </p>
        )}
      </Section>

      {scan.notes.length > 0 ? (
        <Section title={t("monitoring.security.notesTitle")}>
          <ul className="flex flex-col gap-1 text-[13px]">
            {scan.notes.map((note) => (
              <li key={note} className="crm-sub flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--color-warning)]" strokeWidth={2} />
                <span className="min-w-0">{t(noteKey(note))}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <History history={history} t={t} />
    </div>
  );
}
